import type {
  ChangePasswordInput,
  LoginInput,
  RegisterInput,
  UpdateProfileInput,
  User,
} from "@tm/contracts";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { authApi } from "../../../api/auth.api";
import { ApiError, getErrorMessage, isApiError } from "../../../api/client";
import { queryKeys } from "../../../lib/queryKeys";
import { removeBrowserPushSubscription } from "../../notifications/webPush";

/** The signed-in user (`null` when signed out). This query is the single source of auth state. */
export function useMe() {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: ({ signal }) => authApi.me(signal),
    staleTime: 5 * 60 * 1000,
    // A flaky network or a restarting API should not bounce a signed-in user to an error page:
    // retry transient failures a few times with backoff (a 401 resolves to `null`, never errors).
    retry: (failureCount, error) =>
      !(isApiError(error) && error.status >= 400 && error.status < 500) && failureCount < 4,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
  });
}

/** For components rendered behind RequireAuth, where the user is guaranteed to be loaded. */
export function useCurrentUser(): User {
  const { data } = useMe();
  if (!data) throw new Error("useCurrentUser must be used inside an authenticated route");
  return data;
}

/**
 * Marks the user signed out first (so guards unmount signed-in UI), then drops every other
 * cached query. Clearing first would briefly leave signed-in components without a user.
 */
function signOutLocally(queryClient: QueryClient): void {
  queryClient.setQueryData(queryKeys.me, null);
  queryClient.removeQueries({
    predicate: (query) => !(query.queryKey[0] === "auth" && query.queryKey[1] === "me"),
  });
}

function useSignedIn() {
  const queryClient = useQueryClient();
  return (user: User) => {
    // Start from a clean cache so no data from a previous session survives.
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "auth" });
    queryClient.setQueryData(queryKeys.me, user);
  };
}

export function useLogin() {
  const onSignedIn = useSignedIn();
  return useMutation({
    mutationFn: (input: LoginInput) => authApi.login(input),
    onSuccess: onSignedIn,
  });
}

export function useRegister() {
  const onSignedIn = useSignedIn();
  return useMutation({
    mutationFn: (input: RegisterInput) => authApi.register(input),
    onSuccess: onSignedIn,
  });
}

/**
 * Signs out on the server, then performs a full page navigation to `redirectTo`
 * (usually `/login`). The reload discards all in-memory state from the previous session.
 */
export function useLogout() {
  return useMutation({
    mutationFn: async (redirectTo: string) => {
      // The next person signing in on this browser must not get this account's notifications.
      await removeBrowserPushSubscription();
      await authApi.logout();
      return redirectTo;
    },
    onSuccess: (redirectTo) => {
      window.location.replace(redirectTo);
    },
    onError: (error, redirectTo) => {
      // The session is already gone: that is the outcome the user asked for.
      if (error instanceof ApiError && error.status === 401) {
        window.location.replace(redirectTo);
        return;
      }
      // Anything else means the server may still consider them signed in, so say so.
      toast.error(`Could not sign out: ${getErrorMessage(error)}`);
    },
  });
}

export function useVerifyEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => authApi.verifyEmail(token),
    onSuccess: (user) => {
      // Only update the cached user if the verified account is the one signed in.
      const current = queryClient.getQueryData<User | null>(queryKeys.me);
      if (current?.id === user.id) queryClient.setQueryData(queryKeys.me, user);
    },
  });
}

export function useResendVerification() {
  return useMutation({ mutationFn: () => authApi.resendVerification() });
}

export function useForgotPassword() {
  return useMutation({ mutationFn: (email: string) => authApi.forgotPassword(email) });
}

export function useResetPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { token: string; password: string }) =>
      authApi.resetPassword(input.token, input.password),
    onSuccess: () => {
      // The server revokes every session on reset.
      signOutLocally(queryClient);
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProfileInput) => authApi.updateProfile(input),
    onSuccess: (user) => {
      queryClient.setQueryData(queryKeys.me, user);
    },
  });
}

export function useChangePassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ChangePasswordInput) => authApi.changePassword(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.sessions }),
  });
}

export function useSessions() {
  return useQuery({
    queryKey: queryKeys.sessions,
    queryFn: ({ signal }) => authApi.listSessions(signal),
  });
}

export function useRevokeSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => authApi.revokeSession(sessionId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.sessions }),
  });
}
